import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { GraduationCap, Loader2, Mail, Lock } from "lucide-react";

const OWL_LOGO = "https://customer-assets-39nsmqrw.emergentagent.net/job_gts-attendance/artifacts/ju2vrway_LOGO%20IEC.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Terjadi kesalahan. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Selamat datang, ${data.name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data.message || "Tautan reset password telah dikirim jika email terdaftar.");
      setShowForgot(false);
      setForgotEmail("");
    } catch (err) {
      toast.error("Gagal mengirim. Coba lagi.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gtscream">
      <Toaster position="top-center" richColors />
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="classroom"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gtsnavy/90 to-gtsnavy/70" />
        <div className="relative z-10 flex flex-col justify-end p-14 text-white">
          <div className="w-16 h-16 rounded-3xl bg-white flex items-center justify-center mb-6 p-2 shadow-lg">
            <img src={OWL_LOGO} alt="IEC Mustikajaya" className="w-full h-full object-contain" />
          </div>
          <h1 className="font-nunito font-black text-4xl leading-tight mb-3">
            GTS Attendance &<br />Teaching Record
          </h1>
          <p className="text-white/75 text-lg max-w-md">
            Kelola kehadiran, sesi mengajar, dan teaching fee guru IEC Mustikajaya dalam satu aplikasi.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center p-1 shadow">
              <img src={OWL_LOGO} alt="IEC Mustikajaya" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="font-nunito font-extrabold text-gtsnavy text-lg">GTS IEC Mustikajaya</p>
              <p className="text-xs text-muted-foreground">Attendance & Teaching Record</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(16,42,86,0.08)] p-8">
            <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy mb-1">Masuk</h2>
            <p className="text-sm text-muted-foreground mb-6">Silakan login untuk melanjutkan</p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gtsnavy">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@email.com" className="pl-10 rounded-xl"
                    data-testid="login-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-gtsnavy">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                  <PasswordInput
                    id="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" className="pl-10 rounded-xl"
                    testid="login-password"
                  />
                </div>
              </div>
              <div className="flex justify-end -mt-1">
                <button type="button" onClick={() => setShowForgot(true)} className="text-xs text-gtsorange hover:underline font-medium" data-testid="forgot-password-link">
                  Lupa password?
                </button>
              </div>
              <Button
                type="submit" disabled={loading}
                data-testid="login-submit"
                className="w-full rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light hover:opacity-90 text-white font-semibold h-11 transition-transform hover:-translate-y-0.5"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masuk"}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">atau</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <Button
              variant="outline" onClick={handleGoogle}
              data-testid="login-google"
              className="w-full rounded-full h-11 border-2 font-medium gap-2"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
              Masuk dengan Google
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            GTS IEC Mustikajaya · Dibuat secara elektronik
          </p>
        </div>
      </div>

      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent>
          <DialogHeader><DialogTitle>Lupa Password</DialogTitle></DialogHeader>
          <form onSubmit={handleForgot} className="space-y-3">
            <p className="text-sm text-muted-foreground">Masukkan email akun Anda. Kami akan mengirim tautan untuk membuat password baru.</p>
            <div>
              <Label htmlFor="forgot-email">Email</Label>
              <Input id="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="nama@email.com" className="rounded-xl mt-1" data-testid="forgot-email-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={forgotLoading} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white" data-testid="forgot-submit">
                {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kirim Tautan Reset"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
